import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform } from 'react-native';
import type { ReferralEventDto } from '../../storage/WellCheckStorage';

const STATUS_OPTIONS = ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'] as const;
type ReferralStatus = typeof STATUS_OPTIONS[number];

const STATUS_COLORS: Record<ReferralStatus, { bg: string; text: string; border: string }> = {
  'Pending':     { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  'Scheduled':   { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  'In Progress': { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  'Completed':   { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  'Cancelled':   { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
};

const HISTORY_STATUSES: ReferralStatus[] = ['Completed', 'Cancelled'];

const EMPTY: ReferralEventDto = { to: '', for: '', date: '', contactInfo: '', outcome: '', status: 'Pending' };

interface Props {
  events: ReferralEventDto[];
  onUpdate: (events: ReferralEventDto[]) => void;
}

export interface ReferralListEditorHandle {
  openAdd: () => void;
}

export const ReferralListEditor = forwardRef<ReferralListEditorHandle, Props>(
  ({ events, onUpdate }, ref) => {
  const [formVisible, setFormVisible] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<ReferralEventDto>(EMPTY);
  const [showHistory, setShowHistory] = useState(false);
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({ openAdd }));

  const openAdd = () => {
    setDraft({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
    setEditingIdx(null);
    setFormVisible(true);
  };

  const openEdit = (idx: number) => {
    setDraft({ ...events[idx] });
    setEditingIdx(idx);
    setFormVisible(true);
  };

  const cancel = () => {
    setFormVisible(false);
    setEditingIdx(null);
  };

  const save = () => {
    if (!draft.to.trim() && !draft.for.trim()) return;
    const next = [...events];
    if (editingIdx !== null) next[editingIdx] = { ...draft };
    else next.push({ ...draft });
    onUpdate(next);
    setFormVisible(false);
    setEditingIdx(null);
  };

  const remove = (idx: number) => {
    onUpdate(events.filter((_, i) => i !== idx));
    setRemoveConfirmIdx(null);
  };

  const active    = events.map((p, idx) => ({ p, idx })).filter(({ p }) => !HISTORY_STATUSES.includes((p.status || 'Pending') as ReferralStatus));
  const completed = events.map((p, idx) => ({ p, idx })).filter(({ p }) =>  HISTORY_STATUSES.includes((p.status || 'Pending') as ReferralStatus));

  const renderRow = (p: ReferralEventDto, idx: number, isHistory: boolean) => {
    const status = (p.status || 'Pending') as ReferralStatus;
    const sc = STATUS_COLORS[status] ?? STATUS_COLORS['Pending'];
    return (
      <View key={idx} style={[styles.refRow, isHistory && { opacity: 0.75, backgroundColor: '#f9fafb' }]}>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
            {p.to ? (
              <Text style={[styles.refTo, isHistory && { color: '#6b7280' }]}>{p.to}</Text>
            ) : null}
            <View style={{ backgroundColor: sc.bg, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 1, borderWidth: 1, borderColor: sc.border }}>
              <Text style={{ fontSize: 10, color: sc.text, fontWeight: '600' }}>{status}</Text>
            </View>
          </View>
          {p.for ? (
            <Text style={styles.refDetail}>👤 {p.for}</Text>
          ) : null}
          <Text style={styles.refDetail}>
            {[
              p.date        ? `Referred: ${p.date}` : null,
              p.contactInfo ? `Contact: ${p.contactInfo}` : null,
            ].filter(Boolean).join('  ·  ')}
          </Text>
          {p.outcome ? (
            <Text style={[styles.refDetail, { fontStyle: 'italic', color: '#7f8c8d' }]}>{p.outcome}</Text>
          ) : null}
        </View>
        <TouchableOpacity onPress={() => openEdit(idx)} style={styles.refActionBtn}>
          <Text style={styles.refActionText}>Edit</Text>
        </TouchableOpacity>
        {!isHistory && (
          removeConfirmIdx === idx ? (
            <View style={{ flexDirection: 'row', gap: 4 }}>
              <TouchableOpacity onPress={() => remove(idx)} style={styles.refActionBtn}>
                <Text style={[styles.refActionText, { color: '#e74c3c', fontWeight: '700' }]}>Confirm?</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setRemoveConfirmIdx(null)} style={styles.refActionBtn}>
                <Text style={styles.refActionText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity onPress={() => setRemoveConfirmIdx(idx)} style={styles.refActionBtn}>
              <Text style={[styles.refActionText, { color: '#e74c3c' }]}>Remove</Text>
            </TouchableOpacity>
          )
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Active referrals */}
      {active.length === 0 ? (
        <Text style={styles.emptyText}>No active referrals on record.</Text>
      ) : (
        active.map(({ p, idx }) => renderRow(p, idx, false))
      )}

      {/* Completed / Cancelled history */}
      {completed.length > 0 && (
        <>
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 4 }}
            onPress={() => setShowHistory(v => !v)}
          >
            <Text style={{ fontSize: 12, color: '#6b7280', fontWeight: '600' }}>
              {showHistory ? '▾' : '▸'} Completed / Cancelled ({completed.length})
            </Text>
          </TouchableOpacity>
          {showHistory && completed.map(({ p, idx }) => renderRow(p, idx, true))}
        </>
      )}

      {/* Add button — removed; triggered externally via ref */}

      {/* Inline add / edit form */}
      {formVisible && (
        <View style={styles.form}>
          <Text style={styles.formTitle}>
            {editingIdx !== null ? '✏️ Edit Referral' : '➕ Add Referral'}
          </Text>

          {/* Referred To */}
          <Text style={styles.fieldLabel}>Referred To (Specialist / Clinic) *</Text>
          <TextInput
            style={styles.input}
            value={draft.to}
            onChangeText={v => setDraft(d => ({ ...d, to: v }))}
            placeholder="e.g. Dr. Smith - Pediatric Cardiology"
            placeholderTextColor="#aaa"
          />

          {/* Referred For */}
          <Text style={styles.fieldLabel}>Referred For (Reason) *</Text>
          <TextInput
            style={styles.input}
            value={draft.for}
            onChangeText={v => setDraft(d => ({ ...d, for: v }))}
            placeholder="e.g. Cardiac evaluation"
            placeholderTextColor="#aaa"
          />

          {/* Date Referred */}
          <Text style={styles.fieldLabel}>Date Referred</Text>
          {Platform.OS === 'web' ? (
            // @ts-ignore — web-only input
            <input
              type="date"
              value={draft.date}
              onChange={(e: any) => setDraft(d => ({ ...d, date: e.target.value }))}
              style={{
                width: 160, border: '1px solid #d0d7de', borderRadius: 6,
                padding: '6px 10px', fontSize: 13, color: '#2c3e50',
                backgroundColor: '#fafbfc', outline: 'none', marginBottom: 8,
              }}
            />
          ) : (
            <TextInput
              style={[styles.input, { maxWidth: 160 }]}
              value={draft.date}
              onChangeText={v => setDraft(d => ({ ...d, date: v }))}
              placeholder="YYYY-MM-DD"
              placeholderTextColor="#aaa"
            />
          )}

          {/* Contact Info */}
          <Text style={styles.fieldLabel}>Contact Info</Text>
          <TextInput
            style={styles.input}
            value={draft.contactInfo}
            onChangeText={v => setDraft(d => ({ ...d, contactInfo: v }))}
            placeholder="Phone / fax / email"
            placeholderTextColor="#aaa"
          />

          {/* Status chips */}
          <Text style={styles.fieldLabel}>Status</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {STATUS_OPTIONS.map(s => {
              const sel = (draft.status || 'Pending') === s;
              const sc = STATUS_COLORS[s];
              return (
                <TouchableOpacity
                  key={s}
                  style={{
                    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, borderWidth: 1,
                    borderColor: sel ? sc.border : '#d0d7de',
                    backgroundColor: sel ? sc.bg : '#fafbfc',
                  }}
                  onPress={() => setDraft(d => ({ ...d, status: s }))}
                >
                  <Text style={{ fontSize: 12, color: sel ? sc.text : '#5a6472', fontWeight: sel ? '600' : '400' }}>
                    {s}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Notes / Outcome */}
          <Text style={styles.fieldLabel}>Notes / Outcome</Text>
          <TextInput
            style={[styles.input, { minHeight: 60 }]}
            value={draft.outcome}
            onChangeText={v => setDraft(d => ({ ...d, outcome: v }))}
            placeholder="Appointment date, outcome, follow-up notes..."
            placeholderTextColor="#aaa"
            multiline
          />

          {/* Save / Cancel buttons */}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
            <TouchableOpacity style={styles.saveBtn} onPress={save}>
              <Text style={styles.saveBtnText}>Save</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.cancelBtn} onPress={cancel}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  emptyText: {
    fontSize: 13,
    color: '#95a5a6',
    fontStyle: 'italic',
    marginBottom: 8,
  },
  refRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e8ecef',
    marginBottom: 6,
  },
  refTo: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2c3e50',
  },
  refDetail: {
    fontSize: 11,
    color: '#7f8c8d',
    marginTop: 1,
  },
  refActionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  refActionText: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '500',
  },
  addBtn: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#3b82f6',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  addBtnText: {
    fontSize: 13,
    color: '#3b82f6',
    fontWeight: '600',
  },
  form: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#93c5fd',
  },
  formTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e3a5f',
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  saveBtn: {
    flex: 1,
    backgroundColor: '#3b82f6',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
  },
  saveBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  cancelBtnText: {
    color: '#5a6472',
    fontSize: 13,
    fontWeight: '500',
  },
});
